/**
 * Documents Master — document categories and the fields to extract from each.
 *
 * Backed by `/api/v1/document-schemas` (collection: document_schema_master).
 *
 * The backend reads BOTH field shapes — the hand-seeded rows carry display-cased
 * keys ("Field Name") and a free-text Required ("Yes") — and always answers in
 * the camelCase/boolean shape, so nothing here has to know the old one.
 */
import { api } from './api';
import type { PaginatedResponse } from '../types';
import type {
  DocumentCategory,
  DocumentCategoryInput,
  DocumentLanguage,
} from '../types/document-intelligence.types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface DocumentCategoryQueryParams {
  search?: string;
  include_deleted?: boolean;
  is_active?: boolean;
  page?: number;
  page_size?: number;
  /** category | isActive | createdAt | updatedAt | fieldCount */
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
  /** Filter to one accessible project (intersected server-side). */
  app_id?: string;
  /** Only categories written for this language; omit for every language. */
  language?: DocumentLanguage;
}

const BASE = '/api/v1/document-schemas';

export const documentCategoriesService = {
  list: async (params: DocumentCategoryQueryParams = {}): Promise<PaginatedResponse<DocumentCategory>> => {
    const response = await api.get<PaginatedResponse<DocumentCategory>>(`${BASE}/list`, { params });
    return response.data;
  },

  getById: async (id: string): Promise<DocumentCategory> => {
    const response = await api.get<ApiResponse<DocumentCategory>>(`${BASE}/get/${id}`);
    return response.data.data;
  },

  create: async (payload: DocumentCategoryInput): Promise<DocumentCategory> => {
    const response = await api.post<ApiResponse<DocumentCategory>>(`${BASE}/create`, payload);
    return response.data.data;
  },

  update: async (id: string, payload: DocumentCategoryInput): Promise<DocumentCategory> => {
    const response = await api.put<ApiResponse<DocumentCategory>>(`${BASE}/update/${id}`, payload);
    return response.data.data;
  },

  /** Soft delete, as every master in this platform does. */
  delete: async (id: string): Promise<null> => {
    const response = await api.delete<ApiResponse<null>>(`${BASE}/delete/${id}`);
    return response.data.data;
  },

  /** Restore a soft-deleted category. */
  restore: async (id: string): Promise<DocumentCategory> => {
    const response = await api.patch<ApiResponse<DocumentCategory>>(`${BASE}/restore/${id}`);
    return response.data.data;
  },

  /** Change the owning project (null/empty = global). Platform admin only. */
  rescopeProject: async (id: string, appId: string | null): Promise<DocumentCategory> => {
    const response = await api.patch<ApiResponse<DocumentCategory>>(
      `${BASE}/${id}/project`, { appId: appId || null },
    );
    return response.data.data;
  },
};
